import {
  FeedbackDetail,
  FeedbackListPage,
  FeedbackNote,
  FeedbackSettingsView,
  type AddFeedbackNote,
  type ContactPress,
  type DeleteScreenshot,
  type TeamReply,
  type UpdateFeedbackSettings,
  type UpdateTriage,
} from '@myrewards/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { serverQuery, type FeedbackFilters } from '../admin/feedback/filters';
import { request, requestDownload } from './client';

// Settings › System administration › Feedback (APP-104 §6, §7): System Admins and Support.

const root = '/api/v1/admin/feedback';
const one = (displayId: string): string => `${root}/${encodeURIComponent(displayId)}`;

/** An FB reference such as FB-2026-0014; anything else is not a report. */
export const DISPLAY_ID = /^FB-\d{4}-\d{4,}$/;

export const feedbackKeys = {
  all: ['feedback-review'] as const,
  list: (filters: FeedbackFilters, page: number, size: number) =>
    ['feedback-review', 'list', filters, page, size] as const,
  one: (displayId: string) => ['feedback-review', 'one', displayId] as const,
  settings: ['feedback-review', 'settings'] as const,
};

export function feedbackListQuery(
  filters: FeedbackFilters,
  paging: { page: number; size: number },
) {
  return queryOptions({
    queryKey: feedbackKeys.list(filters, paging.page, paging.size),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams(serverQuery(filters));
      params.set('page', String(paging.page));
      params.set('size', String(paging.size));
      return request(`${root}?${params.toString()}`, { schema: FeedbackListPage, signal });
    },
    staleTime: 15_000,
  });
}

/**
 * One report, read now. Opening it is audited (once per reviewer per day) and marks the sender's
 * replies read for the team (APP-138 §D11).
 */
export function readFeedbackDetail(displayId: string, signal?: AbortSignal) {
  return request(one(displayId), {
    schema: FeedbackDetail,
    ...(signal !== undefined && { signal }),
  });
}

/** One report. Opening it is audited (once per reviewer per day), so it isn't re-read behind them. */
export function feedbackDetailQuery(displayId: string) {
  return queryOptions({
    queryKey: feedbackKeys.one(displayId),
    queryFn: ({ signal }) => readFeedbackDetail(displayId, signal),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export const feedbackSettingsQuery = queryOptions({
  queryKey: feedbackKeys.settings,
  queryFn: ({ signal }) => request(`${root}/settings`, { schema: FeedbackSettingsView, signal }),
  staleTime: 30_000,
});

export function saveTriage(displayId: string, body: UpdateTriage) {
  return request(one(displayId), { method: 'PATCH', body, schema: FeedbackDetail });
}

export function addNote(displayId: string, body: AddFeedbackNote) {
  return request(`${one(displayId)}/notes`, { method: 'POST', body, schema: FeedbackNote });
}

/** The team's reply, with the status to set as it goes (APP-138 §D2); the report back. */
export function sendTeamReply(displayId: string, body: TeamReply) {
  return request(`${one(displayId)}/replies`, { method: 'POST', body, schema: FeedbackDetail });
}

/**
 * Notes on the report that a reviewer opened Outlook or Teams (§D18). It finishes even as the
 * browser moves to the new tab, so the caller opens the link right after starting it.
 */
export function noteContact(displayId: string, body: ContactPress) {
  return request(`${one(displayId)}/contacts`, {
    method: 'POST',
    body,
    schema: z.undefined(),
    keepalive: true,
  });
}

export function deleteScreenshot(displayId: string, body: DeleteScreenshot) {
  return request(`${one(displayId)}/screenshot`, {
    method: 'DELETE',
    body,
    schema: z.undefined(),
  });
}

export function saveFeedbackSettings(body: UpdateFeedbackSettings) {
  return request(`${root}/settings`, { method: 'PUT', body, schema: FeedbackSettingsView });
}

/** The same-origin paths the page links to; the server names the files (the FB number only). */
export function screenshotPath(displayId: string, download = false): string {
  return `${one(displayId)}/screenshot${download ? '?download=1' : ''}`;
}
export function bundlePath(displayId: string): string {
  return `${one(displayId)}/bundle`;
}

/** The report as Markdown, to copy. */
export async function markdownText(displayId: string): Promise<string> {
  const blob = await requestDownload(`${one(displayId)}/markdown`, {
    contentType: 'text/markdown',
    maxBytes: 1024 * 1024,
  });
  return blob.text();
}
